<template>
  <!-- eslint-disable  max-len -->
  <div class="modal-parent">
    <div
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <div class="modal_topbar">
        <div class="modal_topbar-title">
          Add new credential for {{ `${user.firstName} ${user.lastName}` }}
        </div>
        <div class="modal_topbar_actions">
          <template v-if="loading || fetching">
            <loader :size="25" />
          </template>
          <template v-else>
            <button
              v-tooltip="{ content: 'Refresh List' }"
              class="btn__icon"
              @click="fetchCards"
            >
              <span
                class="btn__icon-image btn__icon-image--rotate"
                v-html="refreshIcon"
              />
            </button>
          </template>
          <span
            class="modal_topbar-exit"
            @click="closeModal"
            v-html="closeIcon"
          />
        </div>
      </div>
      <div class="modal_section__horizontal mb-1">
        <div class="modal_topbar__description">
          <p class="m-0">
            Only recent transactions generated by User IDs or Cards that have not been assigned to a
            user will be listed here.
          </p>
        </div>
        <div class="help">
          <div class="help__item">
            How to Add:
            <a
              class="help__link"
              target="_blank"
              href="https://bit.ly/3SMqnvA"
            > Card </a>
            |
            <a
              class="help__link"
              target="_blank"
              href="https://bit.ly/47D72Rr"
            > User ID </a>
            |
            <a
              class="help__link"
              target="_blank"
              href="https://bit.ly/47itekc"
            > PIN </a>
            |
            <a
              class="help__link"
              target="_blank"
              href="https://bit.ly/49EtCel"
            > Fingerprint </a>
          </div>
        </div>
      </div>
      <div class="manage-cards-container">
        <template v-if="cardsList.length === 0">
          <div class="empty-state">
            <template v-if="fetching">
              Fetching recent transactions...
            </template>
            <template v-else>
              <p>No recent transactions.</p>
              <p>
                To add a credential for this user, tap their Card or enter their User ID, refresh
                the list, then select the desired Card or User ID.
              </p>
            </template>
          </div>
        </template>
        <template v-else>
          <table class="manage-cards__table">
            <tr>
              <th class="manage-cards__table-cell">
                Time
              </th>
              <th class="manage-cards__table-cell">
                Door Name
              </th>
              <th class="manage-cards__table-cell manage-cards__table-cell--right" />
            </tr>
            <tr
              v-for="(card, index) in cardsList"
              :key="index"
            >
              <th class="manage-cards__table-cell">
                <strong>{{ card.lastUsedAt | date }}</strong>
              </th>
              <th class="manage-cards__table-cell">
                {{ card.lastUsedDeviceName }}
              </th>
              <th class="manage-cards__table-cell manage-cards__table-cell--right">
                <button
                  class="btn btn__text"
                  @click="submitCard(card)"
                >
                  ADD
                </button>
              </th>
            </tr>
          </table>
        </template>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import feather from 'feather-icons'
import Loader from './loader'
import cardQueries from '@/graphql/cards.js'

export default {
  name: 'ManageCards',
  components: {
    loader: Loader,
  },
  mixins: [cardQueries],
  props: ['user', 'loading'],
  data() {
    return {
      fetching: false,
      cardsList: [],
      closeIcon: feather.icons.x.toSvg(),
      refreshIcon: feather.icons['refresh-cw'].toSvg(),
    }
  },
  created() {
    this.fetchCards()
  },
  methods: {
    async fetchCards() {
      this.fetching = true
      try {
        const dayInMilliseconds = 24 * 60 * 60 * 1000
        const fromDate = new Date(Date.now() - dayInMilliseconds).toISOString()
        const toDate = new Date(Date.now()).toISOString()
        this.cardsList = await this.listUnassignedCardsQuery(fromDate, toDate)
        this.fetching = false
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to retrieve cards.')
        console.log('Unable to retrieve cards.', error)
      }
    },
    closeModal() {
      this.$emit('close')
    },
    getTwoFactorAuthOfUser() {
      /*
       * If any cards already assigned to the user have the property
       * isBypass = false, this means 2FA is enabled.
       * We maintain the same setting on the new card assigned to the user.
       * Note: isBypass - True, means 2FA not enabled, False, means enabled
       * In case the user has no cards or the field is undefined we default to
       * disable 2FA
       */
      return this.user.cards === undefined
        ? true
        : this.user.cards.every((card) => {
          return card.isBypass
        })
    },
    submitCard(card) {
      const _card = {
        ...card,
        username: this.user.username,
        isBypass: this.getTwoFactorAuthOfUser(),
      }
      this.$emit('add-card', _card)
    },
    isWithinLast24Hours(card) {
      const hourInMilliseconds = 60 * 60 * 1000
      const cardDate = new Date(card.lastUsedAt)
      const now = new Date()

      const differenceInHours = (now - cardDate) / hourInMilliseconds

      return differenceInHours <= 24 && differenceInHours >= 0
    },
  },
}
</script>

<style scoped lang="scss">
.manage-cards {
  &-container {
    width: 100%;
    max-height: 500px;
    overflow-y: auto;
  }
  &__table {
    border-collapse: collapse;
    width: 100%;
    max-height: 100px;
    &-cell {
      font-weight: normal;
      text-align: left;
      padding: 8px;
      border-bottom: 1px solid $light-grey;
      &--right {
        text-align: right;
      }
    }
  }
}
.help {
  @include flex-display();
  @include flex-justify-content(right);

  font-size: $tiny-font;
  font-weight: 600;
  color: $green;
  &__item {
    @include flex-display();
    @include flex-align-items(center);
  }
  &__link {
    margin-left: 1em;
    margin-right: 1em;
    color: $dark-grey;
  }
}
</style>
